import { getFormattedDuration } from "@msys/formatting";
import type { TranslationKey } from "@msys/tolgee";
import { useFormatting } from "@msys/ui";
import { useTranslate } from "@tolgee/react";
import moment from "moment";
import { useMemo } from "react";
import type { ChangeLogOperation } from "../../../clients/graphqlTypes.js";
import {
  type ChangeLogActionGroup,
  classifyActionGroupEntities,
  describeFieldValue,
  getActionGroupEntities,
  isRootEntityType,
  type PrimaryActionEntity,
} from "./changeLogGrouping.js";
import type {
  ChangeLogActor,
  ChangeLogEntity,
  ChangeLogEntityStrategy,
  ChangeLogEntry,
} from "./changeLogTypes.js";

/**
 * Turn a technical identifier (`materialBuyingPrice`, `pricing.priceTotal`) into a
 * readable label: drop any satellite prefix, split camelCase, sentence-case.
 */
export function humanizeIdentifier(identifier: string): string {
  const leaf = identifier.split(".").at(-1) ?? identifier;
  const spaced = leaf
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * The entity-name-bearing field codes (design D2/D3): an entry on one of these fields
 * carries the entity's own display name as its value. Mirrors the read model's
 * captured-name allowlist (`NAME_FIELD_TYPE_PATTERN` in `buildDataloaders`), anchored on
 * the WHOLE field code so a namespaced satellite (`product.title` — the product's title,
 * not the item's own name) is excluded. Roots are named here too (design D3): a project
 * root by its `title`, a document (quote) root by its `number`.
 *
 * `name` joins the mirror with the project-contact capture (RF-279): a contact link row has
 * no name column, so `linkProjectAndCrmUser` snapshots the CRM person's display name as an
 * ordinary `name` VALUE, and the read model's pattern gained `name` for exactly that. Keeping
 * the mirror in step is what the tier is FOR — it lets a contact action name the contact from
 * its own entries (the name AS OF the action, so an unlink still reads the name the person had
 * then) instead of falling through to the live-resolved `entity.name`. `name` is captured by
 * the contact policy alone, so no other kind changes behaviour.
 */
const NAME_FIELDS = new Set([
  "title",
  "productTitle",
  "userName",
  "filename",
  "number",
  "name",
]);

/**
 * The reference-value field codes whose read-model VALUE resolves through the backend's
 * curated registry (`resolveChangeLogFieldLabel` in `apps/backend/src/lib/buildDataloaders.ts`,
 * design D6) — the frontend mirror that lets `ChangeLogDetailModal`'s `renderValue`
 * distinguish "the read model tried to resolve this and missed" (renders the unresolvable-
 * reference `—`) from "this code was never going to carry a label at all" (falls straight
 * through to `fieldValueText`/the raw render, on the same contract as any other value).
 *
 * Mirrors the registry's switch cases EXACTLY, on the `NAME_FIELDS` precedent above: the five
 * pre-existing codes (`phase`, `lead`, `role_ids`, `assigneeMembershipId`, `buildingItemId`)
 * plus the thirteen the registry gained resolving user/document/organisation/address
 * references (`contactPersonId`, `crmUserId`, `whoId`, `assigneeId`, `authorId`,
 * `completedById`, `createdById`, `originItemId`, `originQuoteId`, `subRequirementId`,
 * `crmSupplierId`, `supplierId`, `deliveryAddressId`). `linkedCrmUserId`/
 * `linkedCrmOrganisationId` are deliberately ABSENT — the cross-tenant CRM contact links stay
 * value-less at capture and never reach the registry, so their raw value never renders at all
 * (the `hidden` state handles them, not this set).
 */
export const REFERENCE_VALUE_FIELDS: ReadonlySet<string> = new Set([
  "phase",
  "lead",
  "role_ids",
  "assigneeMembershipId",
  "buildingItemId",
  "contactPersonId",
  "crmUserId",
  "whoId",
  "assigneeId",
  "authorId",
  "completedById",
  "createdById",
  "originItemId",
  "originQuoteId",
  "subRequirementId",
  "crmSupplierId",
  "supplierId",
  "deliveryAddressId",
]);

/**
 * The value an entry recorded AS OF its action: its new value, else its old value (a delete
 * snapshot, whose `newValue` is not-recorded rather than recorded-empty). Read per side
 * through the tri-state renderer, so a masked / not-recorded side contributes nothing.
 * `undefined` when neither side recorded a usable value (a lifecycle marker, or a recorded
 * but empty/whitespace-only/`[]` new value — RF-262 value-rendering D1 widened `empty` past
 * `null`/`undefined`, so a genuine rename-to-empty must NOT fall back to the stale old value
 * the way an actually-unrecorded delete snapshot does).
 */
function actionFieldValue(entry: ChangeLogEntry): unknown {
  const newValue = describeFieldValue(entry.newValue);
  if (newValue.kind === "value") return newValue.value;
  if (newValue.kind === "empty") return undefined;
  const oldValue = describeFieldValue(entry.oldValue);
  if (oldValue.kind === "value") return oldValue.value;
  return undefined;
}

/**
 * The wire `entityType`s whose display name the frontend RE-COMPOSES rather than reads off
 * `entity.name` (RF-279 D6 / target-naming D4, see {@link useChangeLogLabels}'s
 * `compositeEntityName`): a work session's time span, qualified by its `isBreak`/
 * `isCorrection` flags, and — since target-naming — a plan session's plain span (no
 * break/correction shape at all).
 */
const WORK_SESSION_ENTITY_TYPE = "WORKSESSION";
const PLAN_SESSION_ENTITY_TYPE = "PLANSESSION";

/**
 * Work-session time formats, taken verbatim from the existing timesheet UI so the change log
 * reads like the screen it reports on: `TimeCalculationBox` renders a session's span as
 * `HH:mm – HH:mm` (en dash) and its day as `DD.MM`. The span carries the day too — the log
 * shows sessions long after they were tracked (and a correction of a past day), so a bare
 * wall-clock time would be ambiguous; the backend's ISO composite carries the date for the
 * same reason.
 */
const WORK_SESSION_TIME_FORMAT = "HH:mm";
const WORK_SESSION_DATE_FORMAT = "DD.MM";
const WORK_SESSION_DATE_TIME_FORMAT = `${WORK_SESSION_DATE_FORMAT} ${WORK_SESSION_TIME_FORMAT}`;
/**
 * The year-carrying day format, for a `date` VALUE CELL — which stands alone in a table
 * column with no span around it to date it, so unlike the composite name (whose timesheet
 * parity is the point) it keeps the year.
 */
const WORK_SESSION_DAY_FORMAT = `${WORK_SESSION_DATE_FORMAT}.YYYY`;

/**
 * The scalar sources a session's viewer-local span is composed from — the frontend mirror
 * of the read model's `COMPOSITE_NAME_FIELDS` (`from` / `till` / `date` / `duration`),
 * shaped like the wire `nameSpan` (target-naming design D5: `durationMinutes` there,
 * `duration` here — the field this formatter has always taken).
 */
type WorkSessionSpanSource = {
  from: string | null;
  till: string | null;
  date: string | null;
  duration: number | null;
};

/**
 * A work session's `isBreak`/`isCorrection` qualifier flags, read from THIS action's own
 * entries (design D4) — the one part of the composite name `nameSpan` cannot carry (it is
 * span sources only, {@link spanFromNameSpan}), so a facet chip (no entries at all) never
 * shows the qualifier, only the plain span. First-seen wins, matching `actionEntityName`'s
 * precedence. Plan sessions have no such shape (`plan_sessions` carries no `isBreak`/
 * `isCorrection` column at all), so {@link compositeEntityName} never calls this for one.
 */
function collectWorkSessionFlags(
  group: ChangeLogActionGroup,
  entityId: string
): { isBreak: boolean | null; isCorrection: boolean | null } {
  const flags: { isBreak: boolean | null; isCorrection: boolean | null } = {
    isBreak: null,
    isCorrection: null,
  };
  for (const entry of group.entries) {
    if (entry.entity.entityId !== entityId) continue;
    if (entry.field !== "isBreak" && entry.field !== "isCorrection") continue;
    const flag = actionFieldValue(entry);
    const value = typeof flag === "boolean" ? flag : null;
    if (entry.field === "isBreak") flags.isBreak ??= value;
    else flags.isCorrection ??= value;
  }
  return flags;
}

/**
 * A work/plan session's time span in the VIEWER's timezone (RF-279 D6, target-naming D5) —
 * the whole reason the frontend re-composes this name: the server resolves `name` (and now
 * `nameSpan`) at read time and cannot know the viewer's timezone, so a raw echo would be
 * neither readable nor the viewer's wall-clock time. Plain `moment(...)`, exactly like the
 * timesheet UI (there is no project timezone concept — only `organisations.timezone`,
 * unused for session display).
 *
 * Mirrors the read model's degradation order so the two never disagree about WHICH source
 * names a session, only about the timezone: the `from`–`till` span, an open span while the
 * session runs (`from` only, or the `till`-only shape the table's CHECK also permits), else
 * the `date` + `duration` pair a break-correction delta row carries instead of a span (a
 * plan session's `date`/`duration` are always null — `plan_sessions` has no such shape).
 * Null when the source carries nothing at all, so the caller falls back to `entity.name`.
 */
function composeWorkSessionName(sources: WorkSessionSpanSource): string | null {
  const { from, till, date, duration } = sources;
  if (from !== null) {
    const start = moment(from).format(WORK_SESSION_DATE_TIME_FORMAT);
    return till !== null
      ? `${start} – ${moment(till).format(WORK_SESSION_TIME_FORMAT)}`
      : start;
  }
  if (till !== null) return moment(till).format(WORK_SESSION_DATE_TIME_FORMAT);
  // `duration` is a minute count (`work_sessions.duration`), rendered as the timesheet UI
  // renders every duration: `getFormattedDuration` + a bare " h" unit (`TimeCalculationBox`,
  // `itemPrice.ts`). The unit abbreviation is deliberately not a Tolgee key — it is the same
  // in both locales and the codebase hardcodes it everywhere else.
  //
  // `date` is read back in UTC, unlike the span ends above: it is a `date` COLUMN, so it names
  // a calendar day with no instant in it to convert — but the pool's DATE type parser hands
  // capture `moment.utc(v).toISOString()` (`libs/postgres`), so the captured value is that
  // day's UTC midnight. Reading it locally would shift a correction row's day back for every
  // viewer west of UTC; `moment.utc` recovers exactly the day that was stored.
  if (date !== null) {
    const day = moment.utc(date).format(WORK_SESSION_DATE_FORMAT);
    return duration !== null
      ? `${day} (${getFormattedDuration(duration)} h)`
      : day;
  }
  return duration !== null ? `${getFormattedDuration(duration)} h` : null;
}

/**
 * The viewer-local span text for a `WORKSESSION`/`PLANSESSION` entity, sourced from the wire
 * `nameSpan` (target-naming design D5) rather than the action's own entries: `nameSpan` is
 * resolved at READ TIME (live-first, else captured) for every entity of these two kinds —
 * including a facet row (no entries at all) and an entry whose own action touched none of
 * the span fields — so this is the one source that composes correctly on every surface,
 * unlike the old entries-only recomposition it replaces. `null` when the entity carries no
 * `nameSpan` at all (every other kind) or the span itself has no usable source.
 */
function spanFromNameSpan(entity: ChangeLogEntity): string | null {
  const nameSpan = entity.nameSpan;
  if (!nameSpan) return null;
  return composeWorkSessionName({
    from: nameSpan.from ?? null,
    till: nameSpan.till ?? null,
    date: nameSpan.date ?? null,
    duration: nameSpan.durationMinutes ?? null,
  });
}

/**
 * A name string is "present" only if it is non-empty once trimmed (target-naming design D7):
 * an empty/whitespace-only capture (or override) counts as no name at all, so the caller's
 * fallback chain continues instead of rendering a blank Target. Exported so a strategy's OWN
 * home-kind branch (`useQuoteEntityStrategy`'s `ITEM`) can apply the SAME guard before
 * falling through to `entityDisplayLabel` — one definition of "no name", not a second one.
 */
export function nonEmptyName(value: string | null | undefined): string | null {
  return typeof value === "string" && value.trim() !== "" ? value : null;
}

/**
 * The field codes whose captured VALUE is a raw date-time INSTANT: a work session's — and a
 * plan session's — span ends (`timestamptz` columns). Rendered in the VIEWER's zone, since a
 * true instant has a wall clock only once a zone is chosen. Deliberately narrow: the
 * invoice/order date codes (`dueDate`, `openedAt`, `deliveryOfServiceStart`, …) are not
 * touched here.
 */
const INSTANT_VALUE_FIELDS = new Set(["from", "till"]);

/**
 * The field codes whose captured VALUE is a calendar DAY — today only a work session's `date`,
 * the sole field a break-correction delta row is dated by (no other policy captures a `date`
 * column). It needs its own class because its raw form is just as unpresentable as an instant's
 * but its zone rule is the OPPOSITE: the pool's DATE type parser stores the day as
 * `moment.utc(v).toISOString()` (`libs/postgres`), so the captured value is that day's UTC
 * midnight — e.g. `2026-06-15T00:00:00.000Z` — and only a UTC read gives back the day that was
 * actually stored (a local read shifts it west of UTC).
 */
const DAY_VALUE_FIELDS = new Set(["date"]);

/**
 * Display text for a captured field VALUE whose raw form is not presentable (RF-279 D6). The
 * change-log-ui requirement covers a work session's values as well as its composite name, and
 * both its span ends and its `date` arrive as raw ISO instants — machine timestamps, and for
 * the span ends not even the viewer's wall clock — so they render through the same `moment(...)`
 * calls the composite name uses, keeping the value cells and the entity they belong to in
 * agreement: {@link INSTANT_VALUE_FIELDS} viewer-locally, {@link DAY_VALUE_FIELDS} in UTC.
 *
 * Null for every other field code (and for a non-string value), so the caller's existing
 * tri-state / raw-value rendering stands untouched.
 */
export function changeLogFieldValueText(
  field: string | null | undefined,
  value: unknown
): string | null {
  if (field == null) return null;
  const isInstant = INSTANT_VALUE_FIELDS.has(field);
  if (!isInstant && !DAY_VALUE_FIELDS.has(field)) return null;
  if (typeof value !== "string" || value.trim() === "") return null;
  return isInstant
    ? moment(value).format(WORK_SESSION_DATE_TIME_FORMAT)
    : moment.utc(value).format(WORK_SESSION_DAY_FORMAT);
}

/**
 * The three rendering classes a captured date/instant field code falls into (design D5),
 * chosen to match the owning product surface's OWN rendering of the same column — never a
 * raw ISO string:
 *  - `day-utc` — a SQL `date` column, captured as that day's UTC midnight (the pool's DATE
 *    type parser, `libs/postgres`; the same shape {@link DAY_VALUE_FIELDS} above already
 *    reads by), so only a UTC read gives back the stored day.
 *  - `day-local` — a `timestamptz` column the product already renders as a viewer-local
 *    day (`getFormattedDate`).
 *  - `instant` — a `timestamptz` with wall-clock meaning, rendered as a localized date AND
 *    time (`getFormattedDateTime`).
 */
type DateValueClass = "day-utc" | "day-local" | "instant";

/**
 * The curated date-class registry (design D5), keyed by the flat wire field code — the
 * field-code space is collision-free for these codes today (checked against the capture
 * policies in `libs/document-core/src/lib/changeLog/policy.ts` and the init schema); one
 * line to widen to `(entityType, field)` if a future policy ever reuses a code with a
 * different column type. Deliberately separate from {@link INSTANT_VALUE_FIELDS} /
 * {@link DAY_VALUE_FIELDS} above, which pin the work-/plan-session codes' EXACT existing
 * formats (timesheet parity, unchanged by this registry) — this is the NEW machinery beside
 * them, resolved through {@link useChangeLogLabels}'s `fieldValueText` member rather than a
 * standalone pure function, since `day-local`/`instant` need the viewer's locale
 * (`useFormatting()`).
 */
const DATE_VALUE_FIELD_CLASSES: Readonly<Record<string, DateValueClass>> = {
  dueDate: "day-utc",
  doneDate: "day-utc",
  earliestDate: "day-utc",
  earliestStart: "day-utc",
  deliveryOfServiceStart: "day-utc",
  deliveryOfServiceEnd: "day-utc",
  orderedDate: "day-utc",
  arrivalDate: "day-utc",
  paidDate: "day-utc",
  paymentDueDate: "day-utc",
  // `quotes.expiration_date` is a SQL `date` (`apps/backend/src/lib/schema.sql`), so
  // capture supplies the same UTC-midnight shape as the codes above — a viewer-local
  // read would show the previous day west of UTC.
  expirationDate: "day-utc",
  deadline: "day-local",
  openedAt: "day-local",
  paidAt: "day-local",
  cancelledAt: "day-local",
  closedAt: "day-local",
  time: "instant",
  completedAt: "instant",
};

/**
 * The `day-utc` class's own read (design D5): `moment.utc(...).format("L")` — the SAME UTC
 * read {@link changeLogFieldValueText}'s `date` branch already performs for the work-session
 * precedent, swapping its fixed `DD.MM.YYYY` format for the locale-aware `L` token (the
 * global `moment` locale the app sets once from Tolgee — `common/translations/tolgee.tsx`),
 * since this class's value cell has no year-carrying convention of its own to preserve.
 */
function formatDayUtcClass(value: unknown): string | null {
  if (typeof value !== "string" || value.trim() === "") return null;
  return moment.utc(value).format("L");
}

/** A key of the `ChangeLog` Tolgee namespace — the type {@link CURATED_ENUM_LABEL_KEYS}'s
 * values are constrained to, so an entry can only ever name a key that actually exists
 * (the `PlaceholderDescriptionKey`/`TranslationKey<"DocumentReplacements">` precedent in
 * `libs/textutils/src/lib/registry/types.ts`). */
type ChangeLogTranslationKey = TranslationKey<"ChangeLog">;

/**
 * The curated `(field code, value code) → ChangeLog key` enum table (design D5): keyed
 * FIELD-FIRST so a field code shared across entity kinds (`type` is captured by items,
 * invoices AND projects) cannot inherit another vocabulary's labels — only the exact codes
 * listed for that field resolve, so an uncurated code on the SAME field (or any code on a
 * field this table doesn't cover at all) keeps the raw render. The key names ARE the
 * English label text (this repo's "the English text IS the key" convention), all in the
 * `ChangeLog` namespace. Curation breadth is settled: invoice `status` + `type` ONLY — a
 * fourth `type` code (`advance_payment_invoice`) beyond the design doc's three, because the
 * product vocabulary has four. No other captured vocabulary
 * (`projectStateMachineStatus`/`contractType`/`vatVisibility`/`materialPriceHandling`/item
 * `type`/`linkAs`/…) is curated here — each addition is one more row, one more key.
 */
const CURATED_ENUM_LABEL_KEYS: Readonly<
  Record<string, Readonly<Record<string, ChangeLogTranslationKey>>>
> = {
  status: {
    draft: "Draft",
    open: "Open",
    paid: "Paid",
    cancelled: "Cancelled",
  },
  type: {
    partial_invoice: "Partial invoice",
    down_payment_invoice: "Down payment invoice",
    advance_payment_invoice: "Advance payment invoice",
    final_invoice: "Final invoice",
  },
};

/** The subset of {@link useFormatting}'s return value {@link buildChangeLogLabels} needs for
 * its `fieldValueText` member's `day-local`/`instant` classes. Narrowed to exactly these two
 * so `useChangeLogLabels` can pass individually-memoized callbacks (each keyed only on the
 * viewer's locale) as the `useMemo` dependency, instead of `useFormatting()`'s whole return
 * object, which is a fresh reference every render and would defeat the memo entirely. */
type ChangeLogDateFormatting = Pick<
  ReturnType<typeof useFormatting>,
  "getFormattedDate" | "getFormattedDateTime"
>;

/**
 * The change log's user-facing text, composed at read time from stable codes — never
 * a stored sentence or label (design D6). The grid is a SAP-CDHDR-style header list:
 * a derived **action** (a verb-first, colon-qualified label over the group's *primary*
 * entities — those that survive pricing-cascade suppression) and a **target** (the
 * single surviving entity, with its live position and its name AS OF the action, or the
 * aggregate root). The tri-state old→new values live in the detail modal (the CDPOS
 * detail), not in the list. The change-log-owned vocabulary (CRUD verbs, field labels)
 * lives in a dedicated `ChangeLog` namespace (design D10); common quote/item fields reuse
 * existing `QuoteItem`/`Global` keys. The `<verb>: <items> +N` Action label is assembled
 * in code (`composeVerbWithItems`) from those already-localised pieces plus punctuation —
 * never a template Tolgee key, which (under this repo's "English text IS the key"
 * convention) would leak its raw `{…}` placeholders on a resolution miss.
 */
export function useChangeLogLabels() {
  const { t } = useTranslate(["ChangeLog", "QuoteItem", "Global", "Quote"]);
  // Destructured rather than the whole `useFormatting()` object (see
  // {@link ChangeLogDateFormatting}): each of these two is memoized by `useFormatting`
  // itself on the viewer's locale alone, so they stay referentially stable across renders —
  // preserving the "one stable object per `t`" invariant below.
  const { getFormattedDate, getFormattedDateTime } = useFormatting();
  // One stable object per `t` (i.e. per locale / namespace load): the strategies, the
  // grid's column defs and the filter bar's option lists all `useMemo` over this value,
  // so a fresh object every render would silently defeat every one of those memos.
  return useMemo(
    () => buildChangeLogLabels(t, { getFormattedDate, getFormattedDateTime }),
    [t, getFormattedDate, getFormattedDateTime]
  );
}

function buildChangeLogLabels(
  t: ReturnType<typeof useTranslate>["t"],
  formatting: ChangeLogDateFormatting
) {
  // The CRUD verb as a standalone, title-cased label — the Operation column, the modal
  // title, and the per-operation snapshot-table headers. Lives in the `ChangeLog`
  // namespace (design D10), NOT `Global` (whose `Deleted` is "Cancelled"). Null/absent
  // operation (pure event) → null.
  //
  // **Kind-independent** (design D1, reversing RF-279 D5's link-shaped "Added"/"Removed"
  // override): the change log's action vocabulary is exactly Created / Changed: xy /
  // Deleted, regardless of what kind of entity the operation touched. The single
  // `operation` parameter reflects that at the type level — there is no kind axis left to
  // state.
  const operationLabel = (
    operation: ChangeLogEntry["operation"]
  ): string | null => {
    switch (operation) {
      case "CREATED":
        return t("Created", { ns: "ChangeLog" });
      case "CHANGED":
        return t("Changed", { ns: "ChangeLog" });
      case "DELETED":
        return t("Deleted", { ns: "ChangeLog" });
      default:
        return null;
    }
  };

  // The CRUD verb as a lowercase, mid-sentence verb — the piece the modal's lifecycle
  // lines glue after the «entity» (design D6/D8). Its own `ChangeLog` keys (distinct
  // from the title-cased labels): the case difference is a separate key, never a runtime
  // `toLowerCase()`, which would be wrong in German (nouns stay capitalised).
  // Kind-independent on the same contract as {@link operationLabel}. Pure event → null
  // (no verb).
  const operationVerb = (
    operation: ChangeLogEntry["operation"]
  ): string | null => {
    switch (operation) {
      case "CREATED":
        return t("created", { ns: "ChangeLog" });
      case "CHANGED":
        return t("changed", { ns: "ChangeLog" });
      case "DELETED":
        return t("deleted", { ns: "ChangeLog" });
      default:
        return null;
    }
  };

  // The acting identity's display label (target-naming D6), the one rule every actor-showing
  // surface (the grid's User column, the modal's actor line, the filter bar's actor facet)
  // routes through instead of its own hand-rolled fallback. `ChangeLogActor.label` is
  // live-resolved for a `user:` actor and null when that `users` row is gone — the wire's ONLY
  // deleted-user signal — while a `system:`/`integration:` actor always carries its
  // static-registry label. So `label == null && userId != null` IS the deleted-user case, read
  // straight off the wire with no extra query: it renders the localized `ChangeLog`-namespace
  // "Deleted user", never the raw `user:<uuid>` id. "System" stays the fallback only for a
  // label-less NON-user actor — a belt-and-braces branch, since the static registry never
  // actually returns null today.
  const actorLabel = (actor: ChangeLogActor): string => {
    if (actor.label != null) return actor.label;
    if (actor.userId != null) return t("Deleted user", { ns: "ChangeLog" });
    return t("System", { ns: "Global" });
  };

  const fieldLabel = (entry: Pick<ChangeLogEntry, "field">): string => {
    if (!entry.field) return "";
    // Curated map (design D10): captured-allowlist fields → existing quote/item (and
    // generic Global) label keys. Uncurated codes fall back to a humanised technical name.
    switch (entry.field) {
      case "title":
        return t("Title", { ns: "QuoteItem" });
      case "description":
      case "product.description":
        return t("Description", { ns: "QuoteItem" });
      case "type":
        return t("Type", { ns: "QuoteItem" });
      case "quantity":
      case "estimatedQuantity":
      case "actualQuantity":
      case "pricing.quantity":
        return t("Quantity", { ns: "QuoteItem" });
      case "quantityUnit":
      case "pricing.quantityUnit":
        return t("Unit", { ns: "QuoteItem" });
      case "pricing.priceTotal":
        return t("Total price", { ns: "QuoteItem" });
      case "vertical":
        return t("Vertical", { ns: "QuoteItem" });
      case "product.title":
        return t("Title", { ns: "QuoteItem" });
      case "number":
        return t("Number", { ns: "Global" });
      case "binding":
      case "isBinding":
        return t("Binding", { ns: "Global" });
      // The `product.props2` dynamic-key bucket carries no per-key breakdown (an explicit
      // non-goal); its constant label is a curated read-time code label (design D5), not a
      // stored `fieldLabel`.
      case "product.props2":
        return t("product.props2", { ns: "ChangeLog" });

      // The `invoice.self.*` root allowlist (RF-279 design D4/D6). The wire `field` is the
      // type code's NAME segment only (`invoice.self.dueDate` → `dueDate`), so invoice codes
      // share this flat map with the quote/item/order ones: `number`, `title` and `type` are
      // already curated above and read correctly for an invoice too, and `dueDate` is shared
      // with the item/order allowlists (same meaning, one label). Only the codes the invoice
      // allowlist adds are curated here; they live in the change-log-owned `ChangeLog`
      // namespace (design D10) rather than reusing `Invoices`, which the change-log pages do
      // not otherwise load.
      case "status":
        return t("Status", { ns: "ChangeLog" });
      case "dueDate":
        return t("Due date", { ns: "ChangeLog" });
      case "openedAt":
        return t("Issued on", { ns: "ChangeLog" });
      case "paidAt":
        return t("Paid on", { ns: "ChangeLog" });
      case "cancelledAt":
        return t("Cancelled on", { ns: "ChangeLog" });
      case "cancellationReason":
        return t("Cancellation reason", { ns: "ChangeLog" });
      // `contactPersonId` is captured as a raw id (design D4). Its label names the person,
      // not the id — the VALUE still renders as the raw id until the field joins the read
      // model's reference registry (`assigneeMembershipId` & co. in `resolveChangeLogFieldLabel`).
      case "contactPersonId":
        return t("Contact person", { ns: "ChangeLog" });
      case "deliveryOfServiceStart":
        return t("Delivery of service start", { ns: "ChangeLog" });
      case "deliveryOfServiceEnd":
        return t("Delivery of service end", { ns: "ChangeLog" });
      case "paymentTermDuration":
        return t("Payment term", { ns: "ChangeLog" });
      case "showLaborCostSeparately":
        return t("Show labor cost separately", { ns: "ChangeLog" });
      // Value-less marker field (design D4): the multi-KB text blob is captured as a
      // "changed" marker only, so the label is all the row shows.
      case "invoiceTexts":
        return t("Invoice texts", { ns: "ChangeLog" });

      // The `project.todo.*` allowlist (RF-279 D5). Same NAME-segment-only rule as the
      // invoice block above (`project.todo.isDone` → `isDone`), so the todo codes are flat
      // keys in this shared map: `title` / `description` are already curated at the top and
      // read correctly for a todo, and `time` / `completedAt` / `attachments` humanise
      // acceptably ("Time" / "Completed At" / "Attachments") — only the codes whose
      // humanised fallback would be ugly are curated here. The three `*Id` codes are
      // captured as raw ids (or masked, for the cross-tenant CRM links), so their labels
      // name the *referent*, not the id column.
      case "isDone":
        // Deliberately NOT the existing `Global.Done`: its only call site is a modal's
        // *close* **button** (`EditProjectPlanSessionsModal`), so its translation is a
        // button verb ("finish"), not the completion STATE this field names. `Assignee` /
        // `Created by` below DO reuse `Global` — those are already noun labels there.
        return t("Done", { ns: "ChangeLog" });
      case "assigneeId":
        return t("Assignee", { ns: "Global" });
      case "completedById":
        return t("Completed by", { ns: "ChangeLog" });
      case "createdById":
        return t("Created by", { ns: "Global" });
      // Cross-tenant CRM contact links (design D2): captured value-less, so the label is
      // all the row shows — hence naming the relationship rather than the column.
      case "linkedCrmUserId":
        return t("Linked contact", { ns: "ChangeLog" });
      case "linkedCrmOrganisationId":
        return t("Linked company", { ns: "ChangeLog" });

      // The `project.contact.*` / `project.workSession.*` / `project.subcontract.*`
      // allowlists (RF-279 project children). Same NAME-segment-only rule as the blocks
      // above (`project.workSession.isBreak` → `isBreak`), so they are flat keys in this one
      // shared map — which is also why a generic code like `from` / `date` gets ONE label for
      // every kind that captures it (a plan session's span reads the same as a work
      // session's). Curated only where the humanised fallback would be ugly ("Is Break",
      // "Crm User Id", "Sub Requirement Title") or where an EXISTING key in a namespace this
      // hook already loads says it better in both locales — the humanised fallback is
      // English-only, so a free role-matched reuse is strictly better than shipping "From" to
      // a German viewer. `name` → `Global.Name` and `date` → `Global.Date` are exactly that;
      // `duration` keeps its acceptable "Duration" fallback because no such key exists in a
      // loaded namespace and one bare noun does not justify widening the list.
      //
      // The `from`/`till` pair reuses the `Global.From` / `Global.To` range labels the change
      // log's OWN filter bar already uses for its `occurredFrom`/`occurredTo` pickers — same
      // role (the two ends of a span), so the German is right for free. Curating them as a
      // pair matters: "Von" next to an uncurated "Till" would be worse than both untranslated.
      case "name":
        return t("Name", { ns: "Global" });
      case "from":
        return t("From", { ns: "Global" });
      case "till":
        return t("To", { ns: "Global" });
      case "date":
        return t("Date", { ns: "Global" });
      // A contact link's role (`linkAs`) and the local `users` row it points at. Both name the
      // referent rather than the column, on the `contactPersonId` precedent above; the raw
      // `crmUserId` value still renders as an id until the field joins the read model's
      // reference registry, and `linkAs` renders its raw role code (the CRM role labels live
      // behind `useRelationshipRoles`, which the log does not load).
      case "linkAs":
        return t("Role", { ns: "Global" });
      case "crmUserId":
        return t("Contact", { ns: "Global" });
      // A work session's two boolean markers. `isCorrection` marks the synthetic delta row
      // `modifyProjectWorkSession` maintains instead of editing the user's break, so it is
      // labelled by what it IS (a break correction) rather than by the bare column — the only
      // captured path that sets it is the break branch (design D3).
      case "isBreak":
        return t("Break", { ns: "ChangeLog" });
      case "isCorrection":
        return t("Break correction", { ns: "ChangeLog" });
      // The work session's author (who recorded it) reuses the todo allowlist's already
      // curated `Global."Created by"` — the same role, one label. Its `assigneeId` (whose time
      // it is) is curated with the todos above.
      case "authorId":
        return t("Created by", { ns: "Global" });
      // The subcontract allowlist (design D4): three reference ids plus the two display titles
      // snapshotted at the write-once insert site. The ids name their referent (their values
      // are raw uuids); the titles are labelled as titles so an id row and its title row stay
      // distinguishable in the snapshot table — a shared label would collapse them.
      case "originItemId":
        return t("Origin item", { ns: "ChangeLog" });
      case "originQuoteId":
        return t("Origin quote", { ns: "ChangeLog" });
      case "subRequirementId":
        return t("Requirement", { ns: "ChangeLog" });
      case "originItemTitle":
        return t("Origin item title", { ns: "ChangeLog" });
      case "subRequirementTitle":
        return t("Requirement title", { ns: "ChangeLog" });
    }
    return humanizeIdentifier(entry.field);
  };

  // The strongest operation present among one entity's entries: a lifecycle
  // (CREATED/DELETED) wins over a field CHANGED. Null when none carry an operation.
  const strongestOperation = (
    entries: ChangeLogEntry[]
  ): ChangeLogOperation | null => {
    const ops = new Set(
      entries
        .map(e => e.operation)
        .filter((o): o is ChangeLogOperation => o != null)
    );
    if (ops.has("CREATED")) return "CREATED";
    if (ops.has("DELETED")) return "DELETED";
    if (ops.has("CHANGED")) return "CHANGED";
    return null;
  };

  // Compose a verb-first, colon-qualified Action label (design D3) purely IN CODE from an
  // already-localised verb and a list of already-localised items (field labels or entity
  // names): the first two items joined by ", ", then a "+N" overflow marker for the
  // remaining distinct items. The `:` separator, the `, ` join and the `+N` marker are
  // punctuation glued in code — deliberately NOT a Tolgee template key. This repo's
  // convention is "the English text IS the key", so an ICU template like `{verb}: {items}`
  // is not English and would leak its literal placeholders on any resolution miss (e.g. a
  // not-yet-published/CDN-uncached key); composing in code keeps the label readable
  // regardless. An empty item list degrades to the bare verb — a lone lifecycle (the Target
  // already names it) or a pure-pricing action whose only fields were the suppressed
  // `pricing.*` echo.
  const composeVerbWithItems = (verb: string, items: string[]): string => {
    if (items.length === 0) return verb;
    const shown = items.slice(0, 2).join(", ");
    const overflow = items.length - 2;
    const body = overflow > 0 ? `${shown} +${overflow}` : shown;
    return `${verb}: ${body}`;
  };

  // Input-fields-before-derived ordering (design D3, cosmetic): the meaningful edit
  // (title, type, quantity, …) leads the field list; anything uncurated keeps its
  // first-seen position after the prioritised codes. A wrong order only reorders the
  // list — it never claims a false anchor.
  const FIELD_LABEL_PRIORITY = [
    "title",
    "product.title",
    "productTitle",
    "type",
    "quantity",
    "estimatedQuantity",
    "actualQuantity",
    "quantityUnit",
    "description",
    "product.description",
    "vertical",
    "number",
    "binding",
    "isBinding",
  ];

  // The `<fields>` list (design D3): the union of the primary entities' non-`pricing.*`
  // changed field codes (first-seen), ordered input-before-derived, mapped to labels and
  // deduped BY the resulting label (so `title` and `product.title`, both "Title", collapse).
  const fieldItems = (primaries: PrimaryActionEntity[]): string[] => {
    const codes: string[] = [];
    const seenCodes = new Set<string>();
    for (const p of primaries) {
      for (const code of p.changedFields) {
        if (!seenCodes.has(code)) {
          seenCodes.add(code);
          codes.push(code);
        }
      }
    }

    const remaining = [...codes];
    const ordered: string[] = [];
    for (const priorityCode of FIELD_LABEL_PRIORITY) {
      const idx = remaining.indexOf(priorityCode);
      if (idx !== -1) {
        ordered.push(priorityCode);
        remaining.splice(idx, 1);
      }
    }
    ordered.push(...remaining);

    const labels: string[] = [];
    const seenLabels = new Set<string>();
    for (const code of ordered) {
      const label = fieldLabel({ field: code });
      if (label && !seenLabels.has(label)) {
        seenLabels.add(label);
        labels.push(label);
      }
    }
    return labels;
  };

  // The `<names>` list (design D3), kind-qualified through the shared display rule
  // (target-naming D2): each primary entity's name AS OF the action (design D2) when the
  // action recorded one, else the rule's own fallback chain (span → resolved name → kind
  // label) — never the raw entity id, which this list dropped as its last resort.
  const nameItems = (
    group: ChangeLogActionGroup,
    primaries: PrimaryActionEntity[]
  ): string[] =>
    primaries.map(p =>
      entityDisplayLabel(p.entity, actionEntityName(group, p.entity))
    );

  // The VIEWER-LOCAL composite entity name (RF-279 D6, generalised to `PLANSESSION` by
  // target-naming D4), for the kinds whose display name is a composition of several captured
  // values rather than one name field. Sourced from the wire `nameSpan` (target-naming D5)
  // rather than the action's own entries, so — unlike the old entries-only recomposition —
  // it composes on EVERY surface that carries the entity: the (entry-less) filter-bar facet
  // chips and an entry whose own action never touched a span field, not only the surfaces
  // that hold the action's entries.
  //
  // `WORKSESSION` and `PLANSESSION` are the only kinds that reach here: a subcontract's
  // requirement-title name is plain text with no timezone in it, and a contact's name is a
  // single captured value the name tier already owns (`NAME_FIELDS`) — every other kind
  // returns null, so their callers keep the resolved `entity.name` untouched. A plan session
  // has no break/correction shape at all (`plan_sessions` carries no such column), so it is
  // the PLAIN span with no qualifier; a work session's `isBreak`/`isCorrection` still come
  // from THIS action's own entries ({@link collectWorkSessionFlags}) — `nameSpan` carries
  // only the span sources — so a break, and the synthetic break-correction delta row in
  // particular, is not read as an ordinary tracked session; a correction row carries both
  // flags, so it is tested first.
  const compositeEntityName = (
    group: ChangeLogActionGroup,
    entity: ChangeLogEntity
  ): string | null => {
    if (
      entity.entityType !== WORK_SESSION_ENTITY_TYPE &&
      entity.entityType !== PLAN_SESSION_ENTITY_TYPE
    ) {
      return null;
    }
    const span = spanFromNameSpan(entity);
    if (entity.entityType === PLAN_SESSION_ENTITY_TYPE) return span;

    const flags = collectWorkSessionFlags(group, entity.entityId);
    const qualifier = flags.isCorrection
      ? t("Break correction", { ns: "ChangeLog" })
      : flags.isBreak
        ? t("Break", { ns: "ChangeLog" })
        : null;
    if (qualifier === null) return span;
    return span === null ? qualifier : `${qualifier} ${span}`;
  };

  // The entity's name AS OF this action (design D2): the value carried by the action's
  // own name-field entry — its new value, else the old value (a delete snapshot); empty/
  // whitespace-only captures count as no value (target-naming D7), so an empty rename no
  // longer blanks the Target — the loop keeps looking instead of returning early. Never
  // the live / most-recent name, so a historical rename shows the name it had then, not
  // the current one. Null when the action carries no USABLE name field for the entity
  // (e.g. a non-name field change), leaving the caller ({@link entityDisplayLabel}) to
  // fall back down its own chain. A composite-named kind ({@link compositeEntityName}) has
  // no name field at all and is answered from its own value entries first.
  const actionEntityName = (
    group: ChangeLogActionGroup,
    entity: ChangeLogEntity
  ): string | null => {
    const composite = compositeEntityName(group, entity);
    if (composite !== null) return composite;
    for (const entry of group.entries) {
      if (entry.entity.entityId !== entity.entityId) continue;
      if (entry.field == null || !NAME_FIELDS.has(entry.field)) continue;
      const value = actionFieldValue(entry);
      if (typeof value === "string" && value.trim() !== "") return value;
    }
    return null;
  };

  // The curated kind label (target-naming D2): a localized `ChangeLog`-namespace label for
  // the wire `entityType`, so every entity — including a name resolution miss — renders
  // self-describing rather than bare. An unmapped/future kind falls back to a humanised
  // rendering of the type code itself, so nothing ever renders unqualified.
  const entityKindLabel = (entityType: string): string => {
    switch (entityType) {
      case "DOCUMENT":
        return t("Quote", { ns: "ChangeLog" });
      case "REQUIREMENT":
        return t("Requirement", { ns: "ChangeLog" });
      case "INVOICE":
        return t("Invoice", { ns: "ChangeLog" });
      case "ORDER":
        return t("Order", { ns: "ChangeLog" });
      case "PROJECT":
        return t("Project", { ns: "ChangeLog" });
      case "ITEM":
        return t("Item", { ns: "ChangeLog" });
      case "TODO":
        return t("Todo", { ns: "ChangeLog" });
      case "MEMBER":
        return t("Team member", { ns: "ChangeLog" });
      case "CONTACT":
        return t("Contact", { ns: "ChangeLog" });
      case "ATTACHMENT":
        return t("Attachment", { ns: "ChangeLog" });
      case WORK_SESSION_ENTITY_TYPE:
        return t("Work session", { ns: "ChangeLog" });
      case PLAN_SESSION_ENTITY_TYPE:
        return t("Plan session", { ns: "ChangeLog" });
      case "SUBCONTRACT":
        return t("Subcontract", { ns: "ChangeLog" });
      default:
        return humanizeIdentifier(entityType);
    }
  };

  // The universal kind-qualified display name (target-naming D2): every strategy's
  // `entityLabel` for a non-home kind delegates here, as does every surface with no
  // per-kind exemption of its own (the filter bar's facet options). Name precedence:
  // `nameOverride` (the name as of the action, unchanged since RF-279) → the viewer-local
  // span composition sourced from the wire `nameSpan` (target-naming D5 — a no-op for every
  // kind but `WORKSESSION`/`PLANSESSION`, whose `nameSpan` is null) → the read-time-resolved
  // `entity.name`. Each of the three is treated through the same non-empty guard
  // (target-naming D7): an empty/whitespace string counts as no name, so the chain keeps
  // falling through instead of rendering a blank Target.
  //
  // With a name, the label is `Kind: Title` — `<kindLabel>: <position> <name>` when the
  // entity carries a live position — glued IN CODE, never a Tolgee template key (the
  // `composeVerbWithItems` precedent: an ICU template would leak its placeholders on a
  // resolution miss). Without one, it degrades to the kind label alone, suffixed with the
  // localized "(deleted)" qualifier when the entity is deleted (e.g. `Item (deleted)`) —
  // never the raw entity id, which no caller of this function needs to fall back to any
  // more.
  const entityDisplayLabel = (
    entity: ChangeLogEntity,
    nameOverride?: string | null
  ): string => {
    const kindLabel = entityKindLabel(entity.entityType);
    const name =
      nonEmptyName(nameOverride) ??
      spanFromNameSpan(entity) ??
      nonEmptyName(entity.name);
    if (name === null) {
      return entity.isDeleted
        ? `${kindLabel} ${t("(deleted)", { ns: "ChangeLog" })}`
        : kindLabel;
    }
    return `${kindLabel}: ${entity.position ? `${entity.position} ${name}` : name}`;
  };

  // The action's Target (design D6), derived over the *primary* entities (cascade
  // by-products suppressed): the single surviving entity — with its live position and its
  // name-as-of-action — when exactly one survives, else the aggregate root among the
  // group's entities (any root type, so an invoice/order action is named by its own root
  // rather than the coarse aggregate label), else the aggregate label. Returns the label
  // and an optional link.
  const targetDisplay = (
    group: ChangeLogActionGroup,
    entityStrategy: ChangeLogEntityStrategy
  ): { label: string; link: string | undefined } => {
    const { primary } = classifyActionGroupEntities(group);
    if (primary.length === 1) {
      const { entity } = primary[0];
      return {
        label: entityStrategy.entityLabel(
          entity,
          actionEntityName(group, entity)
        ),
        link: entityStrategy.entityLink(entity),
      };
    }
    const root = getActionGroupEntities(group).find(e =>
      isRootEntityType(e.entityType)
    );
    if (root) {
      return {
        // the root gets the same name-as-of-action treatment as a single primary —
        // a create/delete action names the root from its own snapshot entry
        label: entityStrategy.entityLabel(root, actionEntityName(group, root)),
        link: entityStrategy.entityLink(root),
      };
    }
    return { label: entityStrategy.aggregateLabel(), link: undefined };
  };

  // The action's **Action** label (design D3), a verb-first / colon-qualified string
  // derived over the *primary* entities — never a stored trigger. Precedence:
  //  1. the aggregate root itself CREATED/DELETED — a document, invoice or order root →
  //     that lifecycle IS the action (create / delete a quote, open-and-delete an
  //     invoice), verb-only even though item/field snapshots ride along;
  //  2. exactly one primary → its strongest op: CREATED/DELETED verb-only (the Target
  //     names it), else `Changed: <fields>` over its non-pricing fields;
  //  3. several primaries → all CREATED → `Created: <names>`; all DELETED →
  //     `Deleted: <names>`; otherwise `Changed: <fields>` (the union of their fields).
  // The verb vocabulary is kind-independent (design D1), so none of the three branches
  // needs to state a kind any more — a same-kind bulk attach (e.g. two todos) and a
  // mixed-kind one (e.g. a todo plus a member) read identically.
  const actionLabel = (group: ChangeLogActionGroup): string => {
    const root = group.entries.find(e => isRootEntityType(e.entityType));
    if (root?.operation === "CREATED" || root?.operation === "DELETED") {
      return operationLabel(root.operation) ?? "";
    }

    const { primary } = classifyActionGroupEntities(group);
    if (primary.length === 0) return "";

    if (primary.length === 1) {
      const [only] = primary;
      const op = strongestOperation(only.entries);
      if (op === "CREATED" || op === "DELETED") {
        return operationLabel(op) ?? "";
      }
      if (op === "CHANGED") {
        return composeVerbWithItems(
          operationLabel("CHANGED") ?? "",
          fieldItems([only])
        );
      }
      return "";
    }

    const ops = primary.map(p => strongestOperation(p.entries));
    if (ops.every(o => o === "CREATED")) {
      return composeVerbWithItems(
        operationLabel("CREATED") ?? "",
        nameItems(group, primary)
      );
    }
    if (ops.every(o => o === "DELETED")) {
      return composeVerbWithItems(
        operationLabel("DELETED") ?? "",
        nameItems(group, primary)
      );
    }
    return composeVerbWithItems(
      operationLabel("CHANGED") ?? "",
      fieldItems(primary)
    );
  };

  // Display text for a captured field VALUE that is not itself a resolved reference label
  // (design D5) — the hook-member home {@link changeLogFieldValueText} moved into, so
  // booleans and the curated enum table can resolve through `t` and the two locale-aware
  // date classes through `useFormatting()`'s `getFormattedDate`/`getFormattedDateTime`.
  // Resolution order, each a disjoint field/value shape so only one branch ever fires:
  //  1. any boolean, field-independently, → the localized Yes/No (never literal true/false);
  //  2. the work-/plan-session codes (`from`/`till`/`date`) → the UNCHANGED pure helper,
  //     preserving their exact existing formats (timesheet parity);
  //  3. the curated date-class registry ({@link DATE_VALUE_FIELD_CLASSES}) → a localized
  //     day (UTC- or viewer-local-read, per class) or date-time;
  //  4. the curated enum table ({@link CURATED_ENUM_LABEL_KEYS}), keyed field-first so a
  //     shared field code cannot inherit another vocabulary's labels.
  // Null when nothing above matches, so the caller's existing raw-value formatting stands.
  const fieldValueText = (
    field: string | null | undefined,
    value: unknown
  ): string | null => {
    if (typeof value === "boolean") {
      return value ? t("Yes", { ns: "Global" }) : t("No", { ns: "Global" });
    }

    const legacy = changeLogFieldValueText(field, value);
    if (legacy !== null) return legacy;

    if (field == null) return null;

    const dateClass = DATE_VALUE_FIELD_CLASSES[field];
    if (dateClass && typeof value === "string" && value.trim() !== "") {
      switch (dateClass) {
        case "day-utc":
          return formatDayUtcClass(value);
        case "day-local":
          return formatting.getFormattedDate(value);
        case "instant":
          return formatting.getFormattedDateTime(value);
      }
    }

    if (typeof value === "string") {
      const key = CURATED_ENUM_LABEL_KEYS[field]?.[value];
      if (key) return t(key, { ns: "ChangeLog" });
    }

    return null;
  };

  return {
    operationLabel,
    operationVerb,
    actorLabel,
    fieldLabel,
    actionLabel,
    targetDisplay,
    compositeEntityName,
    entityKindLabel,
    entityDisplayLabel,
    fieldValueText,
  };
}
